import { FC } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';

import styles from './DocumentsList.module.scss';
import { TripDocument } from '../../../../../../../types/trip.types';
import { useDeleteDocumetMutation, useGetStopDocumentsQuery } from '../../../../../../../store/services/documentsApi';

interface DocumentsListProps {
  tripStopId: number;
  onRefetchTrip?: () => void;
}

const DocumentsList: FC<DocumentsListProps> = ({ tripStopId, onRefetchTrip }) => {
  const [deleteDocument] = useDeleteDocumetMutation();
  const {
    data: stopDocumentsResponse,
    isLoading,
    refetch,
  } = useGetStopDocumentsQuery(tripStopId, {
    skip: !tripStopId,
    refetchOnMountOrArgChange: true,
  });
  const documents = (stopDocumentsResponse || []) as TripDocument[];

  const handleDeleteDocument = async (documentId: number, event: React.MouseEvent) => {
    event.stopPropagation();

    try {
      await deleteDocument({ id: documentId }).unwrap();

      if (onRefetchTrip) {
        onRefetchTrip();
      }
      refetch();
    } catch (error) {
      console.error('Failed to delete document:', error);
    }
  };

  const getFileExtension = (attachment: string): string => {
    const parts = attachment.split('.');
    return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
  };

  const getFileIcon = (document: TripDocument) => {
    if (document.attachments.length === 0) {
      return <PictureAsPdfIcon className={styles.pdfIcon} />;
    }
    return <PictureAsPdfIcon className={styles.pdfIcon} />;
  };

  if (isLoading) {
    return (
      <Typography
        variant="h12"
        sx={{ color: '#5E6A81' }}
      >
        Loading documents...
      </Typography>
    );
  }

  if (documents.length === 0) {
    return null;
  }

  return (
    <Box className={styles.documentsList}>
      <Typography
        variant="h10Semibold"
        className={styles.sectionTitle}
      >
        Documents
      </Typography>

      <Box className={styles.documentsContainer}>
        {documents.map(document => (
          <Box
            key={document.id}
            className={styles.documentItem}
          >
            <Box className={styles.documentInfo}>
              {getFileIcon(document)}

              <Box className={styles.documentDetails}>
                <Typography
                  variant="h12Semibold"
                  className={styles.documentName}
                >
                  {document.name}
                </Typography>
                {document.attachments.length > 1 && (
                  <Typography
                    variant="h13"
                    className={styles.documentSize}
                  >
                    {document.attachments.length} files
                  </Typography>
                )}
                {document.tags.length > 0 && (
                  <Box className={styles.documentTags}>
                    {document.tags.map(tag => (
                      <Box
                        key={tag.id}
                        className={styles.documentTag}
                        style={{ backgroundColor: tag.color }}
                      >
                        <Typography
                          variant="h13"
                          className={styles.tagText}
                        >
                          {tag.name}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </Box>

            <IconButton
              onClick={e => handleDeleteDocument(document.id, e)}
              className={styles.deleteButton}
              size="small"
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default DocumentsList;
